import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users, HeartPulse, ClipboardCheck, CalendarCheck, ArrowRight, UserPlus, ScanSearch,
  ShieldCheck, TrendingUp, Sparkles, Activity, Building2,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import {
  PageHeader, Panel, StatCard, EmptyState, ErrorBanner, LoadingRows,
  PriorityPill, SpinnerButton, timeAgo,
} from "../../components/app/ui";
import { EASE } from "../../components/landing/shared";

const PRIORITY_DOT = { high: "#DC2626", medium: "#D97706", low: "#64748B" };
const HEALTH_ROWS = [
  { key: "on_track", label: "On Track", color: "#059669", bg: "#10B981" },
  { key: "needs_monitoring", label: "Needs Monitoring", color: "#D97706", bg: "#F59E0B" },
  { key: "at_risk", label: "At Risk", color: "#DC2626", bg: "#EF4444" },
];
const INSIGHT_ICONS = {
  review: ScanSearch,
  behind: TrendingUp,
  approval: ClipboardCheck,
  followup: CalendarCheck,
};
const ACTIVITY_META = {
  review_completed: { icon: ClipboardCheck, label: "AI review completed", color: "#0891B2" },
  patient_created: { icon: UserPlus, label: "Patient added", color: "#2563EB" },
  followup_completed: { icon: CalendarCheck, label: "Follow-up completed", color: "#059669" },
  clinic_updated: { icon: Building2, label: "Clinic profile updated", color: "#6366F1" },
};
const RANGES = [7, 30, 90];

export default function DashboardHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [intel, setIntel] = useState(null);
  const [activity, setActivity] = useState([]);
  const [series, setSeries] = useState([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [error, setError] = useState("");
  const [completingId, setCompletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [statsRes, intelRes, activityRes] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/dashboard/intelligence"),
        api.get("/activity", { params: { limit: 6 } }),
      ]);
      setStats(statsRes.data);
      setIntel(intelRes.data);
      setActivity(activityRes.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    setChartLoading(true);
    api
      .get("/dashboard/progress", { params: { days } })
      .then(({ data }) => {
        if (!cancelled) {
          setSeries(
            data.series.map((d) => ({
              ...d,
              label: new Date(`${d.date}T00:00:00`).toLocaleDateString("en", { month: "short", day: "numeric" }),
            }))
          );
        }
      })
      .catch(() => {})
      .finally(() => !cancelled && setChartLoading(false));
    return () => {
      cancelled = true;
    };
  }, [days]);

  const reviewCase = async (reviewId) => {
    setCompletingId(reviewId);
    try {
      await api.post(`/reviews/${reviewId}/complete`);
      toast.success("Case marked as reviewed.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCompletingId(null);
    }
  };

  const firstName = user?.name?.split(" ")[0] || "Alignexa";
  const health = intel?.health || { on_track: 0, needs_monitoring: 0, at_risk: 0, total: 0 };

  return (
    <div data-testid="dashboard-home">
      <PageHeader
        testId="dashboard-header"
        title={`Good day, Dr. ${firstName}`}
        sub="Your clinic at a glance — AI-powered clinical intelligence from your database."
      />
      <ErrorBanner message={error} onRetry={load} />

      {/* KPI ROW */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4" data-testid="dashboard-stats">
        <StatCard testId="stat-total-patients" icon={Users} label="Total Patients" value={stats?.total_patients ?? 0} sub={`${stats?.active_patients ?? 0} active patients`} accent="#2563EB" loading={loading} />
        <StatCard testId="stat-active-treatments" icon={HeartPulse} label="Active Treatments" value={stats?.active_treatments ?? 0} sub={`${health.on_track} on track`} accent="#6366F1" loading={loading} />
        <StatCard testId="stat-pending-reviews" icon={ClipboardCheck} label="Pending AI Reviews" value={stats?.pending_ai_reviews ?? 0} sub={`${stats?.high_priority_reviews ?? 0} high priority`} accent="#0891B2" loading={loading} />
        <StatCard testId="stat-followups-due" icon={CalendarCheck} label="Follow-ups Due" value={stats?.followups_due ?? 0} sub={`${stats?.overdue_followups ?? 0} overdue`} accent="#059669" loading={loading} />
      </div>

      {/* MAIN ROW */}
      <div className="grid lg:grid-cols-3 gap-5 mt-6">
        <Panel testId="clinical-intelligence-panel" className="lg:col-span-2 p-6 sm:p-7">
          <div className="flex items-center justify-between mb-1.5">
            <h2 className="font-display text-xl tracking-tight text-[#0F172A]">Today's Clinical Intelligence</h2>
            <Link
              to="/reviews"
              data-testid="intelligence-open-queue"
              className="group inline-flex items-center gap-1.5 text-xs font-semibold text-[#2563EB] hover:text-[#1D4ED8] transition-colors"
            >
              Open queue
              <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5" strokeWidth={2} />
            </Link>
          </div>
          <p className="text-sm text-slate-500 mb-6">AI-powered insights requiring your attention.</p>

          {loading ? (
            <LoadingRows rows={3} />
          ) : !intel || intel.cases.length === 0 ? (
            <EmptyState testId="intelligence-empty" title="No cases need attention" sub="New AI-analyzed patient submissions will appear here, prioritized for you." />
          ) : (
            <div className="space-y-3">
              {intel.cases.map((c) => (
                <motion.div
                  key={c.review_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: EASE }}
                  data-testid={`intel-case-${c.review_id}`}
                  className="rounded-xl border border-slate-200/80 bg-[#FAFBFC] p-5 transition-[border-color,box-shadow] duration-300 hover:border-[#2563EB]/25 hover:shadow-[0_10px_26px_rgba(37,99,235,0.08)]"
                >
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <PriorityPill priority={c.priority} />
                    <span className="text-[11px] font-semibold text-slate-400 ml-auto">
                      AI confidence {c.confidence}%
                    </span>
                  </div>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-[200px]">
                      <p className="text-base font-semibold text-[#0F172A]">{c.patient_name}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {c.display_id} · {c.treatment_type} · Stage {c.treatment_stage} of {c.treatment_total_stages}
                      </p>
                      <p className="flex items-center gap-2 text-sm text-slate-600 mt-3">
                        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: PRIORITY_DOT[c.priority] }} />
                        {c.finding}
                      </p>
                      <p className="text-xs text-slate-400 mt-1.5">
                        Recommended action: <span className="font-medium text-slate-600">{c.recommended_action}</span>
                      </p>
                    </div>
                    <SpinnerButton
                      loading={completingId === c.review_id}
                      onClick={() => reviewCase(c.review_id)}
                      data-testid={`review-case-${c.review_id}`}
                      className="rounded-full bg-gradient-to-r from-[#2563EB] to-[#6366F1] px-5 py-2.5 text-xs font-semibold text-white shadow-[0_8px_20px_rgba(37,99,235,0.25)] transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(99,102,241,0.35)]"
                    >
                      Review Case
                    </SpinnerButton>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
          <p className="flex items-center gap-2 text-[11px] text-slate-400 mt-5 pt-4 border-t border-slate-100">
            <ShieldCheck className="w-3.5 h-3.5 text-[#0891B2]" strokeWidth={1.8} />
            AI insights are advisory — you remain responsible for all clinical decisions.
          </p>
        </Panel>

        {/* Treatment Health */}
        <Panel testId="treatment-health-card" className="p-6 sm:p-7">
          <h2 className="font-display text-xl tracking-tight text-[#0F172A] mb-6">Treatment Health</h2>
          {loading ? (
            <div className="space-y-5">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-12 rounded-xl bg-slate-100 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-6">
              {HEALTH_ROWS.map((row, i) => {
                const count = health[row.key] || 0;
                const pct = health.total ? Math.round((count / health.total) * 100) : 0;
                return (
                  <div key={row.key} data-testid={`health-${row.key.replace(/_/g, "-")}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] font-semibold tracking-[0.14em] uppercase" style={{ color: row.color }}>
                        {row.label}
                      </span>
                      <span className="text-xs text-slate-400">
                        <span className="font-semibold text-[#0F172A]">{count}</span> patient{count === 1 ? "" : "s"} · {pct}%
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 1, ease: EASE, delay: 0.2 + i * 0.12 }}
                        className="h-full rounded-full"
                        style={{ backgroundColor: row.bg }}
                      />
                    </div>
                  </div>
                );
              })}
              <p className="text-[11px] text-slate-400 pt-4 border-t border-slate-100">
                Calculated live from treatment progress versus expected stage timelines.
              </p>
            </div>
          )}
        </Panel>
      </div>

      {/* SECONDARY ROW */}
      <div className="grid lg:grid-cols-3 gap-5 mt-5">
        {/* Treatment Progress */}
        <Panel testId="treatment-progress-card" className="lg:col-span-2 p-6 sm:p-7">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div>
              <h2 className="font-display text-xl tracking-tight text-[#0F172A]">Treatment Progress</h2>
              <p className="text-xs text-slate-400 mt-1">Reviews, follow-ups and new patients over time.</p>
            </div>
            <div className="inline-flex gap-1 p-1 rounded-full bg-slate-100 border border-slate-200/70" data-testid="progress-filters">
              {RANGES.map((r) => (
                <button
                  key={r}
                  type="button"
                  data-testid={`progress-filter-${r}d`}
                  onClick={() => setDays(r)}
                  className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors duration-200 ${
                    days === r ? "bg-white text-[#1D4ED8] shadow-sm border border-slate-200" : "text-slate-400 hover:text-slate-600 border border-transparent"
                  }`}
                >
                  {r}D
                </button>
              ))}
            </div>
          </div>
          {chartLoading ? (
            <div className="h-56 rounded-xl bg-slate-100 animate-pulse" />
          ) : (
            <div className="h-56" data-testid="progress-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={series} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gReviews" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2563EB" stopOpacity={0.22} />
                      <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gFollowups" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6366F1" stopOpacity={0.18} />
                      <stop offset="100%" stopColor="#6366F1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94A3B8" }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={40} />
                  <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12, boxShadow: "0 8px 24px rgba(15,23,42,0.08)" }}
                    labelStyle={{ color: "#0F172A", fontWeight: 600 }}
                  />
                  <Area type="monotone" dataKey="reviews" name="AI reviews" stroke="#2563EB" strokeWidth={2} fill="url(#gReviews)" />
                  <Area type="monotone" dataKey="followups" name="Follow-ups done" stroke="#6366F1" strokeWidth={2} fill="url(#gFollowups)" />
                  <Area type="monotone" dataKey="patients" name="New patients" stroke="#0891B2" strokeWidth={1.5} fill="transparent" strokeDasharray="4 4" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>

        {/* AI Insights */}
        <Panel testId="ai-insights-panel" className="p-6 sm:p-7">
          <div className="flex items-center gap-2.5 mb-6">
            <span className="w-8 h-8 rounded-lg bg-[#6366F1]/10 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#6366F1]" strokeWidth={1.8} />
            </span>
            <h2 className="font-display text-xl tracking-tight text-[#0F172A]">AI Insights</h2>
          </div>
          {loading ? (
            <div className="space-y-3">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-11 rounded-xl bg-slate-100 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-2.5">
              {(intel?.insights || []).map((insight) => {
                const Icon = INSIGHT_ICONS[insight.key] || Activity;
                return (
                  <div
                    key={insight.key}
                    data-testid={`insight-${insight.key}`}
                    className="flex items-center gap-3 rounded-xl border border-slate-100 bg-[#FAFBFC] px-4 py-3 transition-colors duration-200 hover:border-[#2563EB]/20"
                  >
                    <Icon className="w-4 h-4 text-[#2563EB] shrink-0" strokeWidth={1.8} />
                    <p className="text-sm text-slate-600 leading-snug">{insight.text}</p>
                  </div>
                );
              })}
              <p className="text-[11px] text-slate-400 pt-3">
                Advisory observations only — never autonomous diagnoses.
              </p>
            </div>
          )}
        </Panel>
      </div>

      {/* BOTTOM ROW */}
      <div className="grid lg:grid-cols-3 gap-5 mt-5">
        {/* Recent Activity */}
        <Panel testId="dashboard-activity" className="lg:col-span-2 p-6 sm:p-7">
          <h2 className="font-display text-xl tracking-tight text-[#0F172A] mb-5">Recent Activity</h2>
          {loading ? (
            <LoadingRows rows={3} />
          ) : activity.length === 0 ? (
            <EmptyState testId="activity-empty" title="No activity yet" sub="Actions in your clinic will be logged here." />
          ) : (
            <div className="divide-y divide-slate-100">
              {activity.map((log) => {
                const meta = ACTIVITY_META[log.action] || { icon: Activity, label: "Activity", color: "#64748B" };
                return (
                  <div key={log.log_id} className="flex items-center gap-4 py-3.5 first:pt-0 last:pb-0" data-testid={`activity-${log.log_id}`}>
                    <span className="w-9 h-9 rounded-xl border border-slate-100 flex items-center justify-center shrink-0" style={{ backgroundColor: `${meta.color}12` }}>
                      <meta.icon className="w-4 h-4" style={{ color: meta.color }} strokeWidth={1.8} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#0F172A]">{meta.label}</p>
                      {log.patient && (
                        <p className="text-xs text-slate-400 mt-0.5">
                          {log.patient.first_name} {log.patient.last_name} · {log.patient.display_id}
                        </p>
                      )}
                    </div>
                    <span className="text-xs text-slate-400 shrink-0">{timeAgo(log.created_at)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>

        {/* Quick Actions */}
        <Panel testId="quick-actions-panel" className="p-6 sm:p-7">
          <h2 className="font-display text-xl tracking-tight text-[#0F172A] mb-5">Quick Actions</h2>
          <div className="space-y-3">
            <button
              type="button"
              data-testid="qa-add-patient"
              onClick={() => navigate("/patients?add=1")}
              className="w-full flex items-center gap-3 rounded-xl bg-gradient-to-r from-[#2563EB] to-[#6366F1] px-5 py-3.5 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(37,99,235,0.25)] transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_30px_rgba(99,102,241,0.35)]"
            >
              <UserPlus className="w-4 h-4" strokeWidth={2} />
              Add Patient
            </button>
            <button
              type="button"
              data-testid="qa-review-cases"
              onClick={() => navigate("/reviews")}
              className="w-full flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-5 py-3.5 text-sm font-semibold text-slate-700 transition-[border-color,color,box-shadow] duration-300 hover:border-[#2563EB]/30 hover:text-[#1D4ED8] hover:shadow-[0_8px_20px_rgba(37,99,235,0.08)]"
            >
              <ScanSearch className="w-4 h-4 text-[#2563EB]" strokeWidth={1.8} />
              Review AI Cases
            </button>
            <button
              type="button"
              data-testid="qa-view-followups"
              onClick={() => navigate("/follow-ups")}
              className="w-full flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-5 py-3.5 text-sm font-semibold text-slate-700 transition-[border-color,color,box-shadow] duration-300 hover:border-[#059669]/30 hover:text-[#047857] hover:shadow-[0_8px_20px_rgba(5,150,105,0.08)]"
            >
              <CalendarCheck className="w-4 h-4 text-[#059669]" strokeWidth={1.8} />
              View Follow-ups
            </button>
          </div>
        </Panel>
      </div>
    </div>
  );
}
